/**
 * usePortalSession — Manages the portal session token in sessionStorage.
 *
 * Token format from the Worker: `licenseId:expiresAt:hmacSignature`
 * Token has a 1-hour TTL. Stored in sessionStorage (cleared on tab close).
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const SESSION_KEY = 'portal_session';
const EXPIRY_CHECK_INTERVAL = 60_000; // 1 minute

/**
 * Parse `licenseId:expiresAt:signature` into structured data.
 * @param {string} token
 * @returns {{ licenseId: string, expiresAt: number, raw: string } | null}
 */
function parseToken(token) {
    if (!token) return null;
    const parts = token.split(':');
    if (parts.length !== 3) return null;
    const [licenseId, expiresAtStr] = parts;
    const expiresAt = parseInt(expiresAtStr, 10);
    if (!licenseId || isNaN(expiresAt)) return null;
    return { licenseId, expiresAt, raw: token };
}

/**
 * @returns {{
 *   token: string | null,
 *   licenseId: string | null,
 *   isAuthenticated: boolean,
 *   isExpired: boolean,
 *   saveSession: (token: string) => void,
 *   clearSession: () => void,
 * }}
 */
export function usePortalSession() {
    const [session, setSession] = useState(() => {
        const stored = sessionStorage.getItem(SESSION_KEY);
        return parseToken(stored);
    });

    const intervalRef = useRef(null);

    const isExpired = session ? Date.now() > session.expiresAt : false;
    const isAuthenticated = session !== null && !isExpired;

    const saveSession = useCallback((token) => {
        const parsed = parseToken(token);
        if (parsed) {
            sessionStorage.setItem(SESSION_KEY, token);
            setSession(parsed);
        }
    }, []);

    const clearSession = useCallback(() => {
        sessionStorage.removeItem(SESSION_KEY);
        setSession(null);
    }, []);

    // Auto-check expiry periodically
    useEffect(() => {
        if (!session) return;

        function checkExpiry() {
            if (Date.now() > session.expiresAt) {
                clearSession();
            }
        }

        intervalRef.current = setInterval(checkExpiry, EXPIRY_CHECK_INTERVAL);
        return () => clearInterval(intervalRef.current);
    }, [session, clearSession]);

    return {
        token: session?.raw ?? null,
        licenseId: session?.licenseId ?? null,
        isAuthenticated,
        isExpired,
        saveSession,
        clearSession,
    };
}
